// Can this browser reach a Midulate at all? (MDL-392)
//
// A new customer is sent to the web editor and, on an iPhone, nothing works.
// Two separate causes, and both had to be fixed:
//
//   1. ConnectionStatus touched navigator.serial unguarded, so the component
//      threw during mount anywhere Web Serial is absent. The connection UI did
//      not merely fail to connect, it broke on arrival.
//
//   2. Even mounted, both transports are Chromium-only. Web Serial ships in
//      Chromium desktop and nowhere else. Reaching http://192.168.4.1 from an
//      HTTPS page needs Private Network Access, which only Chromium
//      implements — Safari and Firefox block it as mixed content.
//
// Every browser on iOS is WebKit underneath, so "use Chrome instead" is not
// advice that helps an iPhone owner. It is also why the access point works:
// the device serves its editor over plain HTTP itself, so there is no mixed
// content and no PNA involved.
//
// Detection is by *engine*, not by feature alone. `'serial' in navigator` is
// the tempting test, but Chrome on Android has no Web Serial while still
// supporting PNA — treating its absence as "cannot reach the device" would
// push Android users to the access point when WiFi works for them.
//
// This lives in the web editor rather than the shared package on purpose: the
// on-device editor is served by the device and has neither problem, so it must
// never show this guidance.

export const TRANSPORT_OK = 'ok';
export const TRANSPORT_ACCESS_POINT_ONLY = 'access-point-only';
export const TRANSPORT_SWITCH_BROWSER = 'switch-browser';

// What a new unit broadcasts, and how to reach it. The suffix cannot be known
// here — it derives from the device's UID (wifi_manager.py), which we cannot
// read precisely because we cannot reach the device.
export const AP_SSID_PREFIX = 'Midulate-';
export const AP_DEFAULT_PASSWORD = 'midulate';
export const AP_ADDRESS = 'http://192.168.4.1';

/**
 * iPhone, iPod or iPad — including iPadOS, which reports itself as a Mac.
 * A Mac with a touchscreen does not exist, so Mac plus touch points is an iPad.
 */
export function isIosLike(nav) {
  const platform = nav?.platform ?? '';
  const ua = nav?.userAgent ?? '';
  if (/iPhone|iPod|iPad/.test(platform) || /iPhone|iPod|iPad/.test(ua)) return true;
  return /Mac/.test(platform) && (nav?.maxTouchPoints ?? 0) > 1;
}

/**
 * A real Chromium engine.
 *
 * Checked only after iOS, because Chrome and Edge there advertise themselves
 * in the user agent (CriOS, EdgiOS) while running on WebKit. Believing them
 * would tell an iPhone owner everything is fine.
 */
export function isChromiumEngine(nav) {
  const brands = nav?.userAgentData?.brands;
  if (Array.isArray(brands) && brands.length) {
    return brands.some((b) => /Chromium/i.test(b?.brand ?? ''));
  }
  const ua = nav?.userAgent ?? '';
  if (/Firefox\/|FxiOS/.test(ua)) return false;
  return /Chrome\/|Chromium\/|Edg\//.test(ua);
}

/** True when Web Serial is actually present — the guard the codebase needed. */
export function hasWebSerial(nav) {
  try {
    return !!nav && typeof nav === 'object' && 'serial' in nav;
  } catch {
    return false;
  }
}

/**
 * What this browser can do, and what to tell the owner.
 *
 * `nav` is injectable so each branch is testable from a snapshot rather than
 * whichever browser the tests happen to run in.
 */
export function detectTransportSupport(nav = globalThis.navigator) {
  if (isIosLike(nav)) {
    // No USB, and the local-network request is blocked. The access point is
    // the only route, and there is no browser they could switch to.
    return {
      platform: 'ios',
      chromium: false,
      webSerial: false,
      wifiToDevice: false,
      advice: TRANSPORT_ACCESS_POINT_ONLY,
    };
  }

  if (!isChromiumEngine(nav)) {
    // Safari or Firefox on a desktop. Switching browser restores both
    // transports, which is better advice than joining an access point.
    return {
      platform: 'desktop',
      chromium: false,
      webSerial: false,
      wifiToDevice: false,
      advice: TRANSPORT_SWITCH_BROWSER,
    };
  }

  // Chromium. WiFi always works; USB only where Web Serial ships, which is
  // desktop — Chrome on Android has PNA but no Web Serial.
  return {
    platform: 'desktop',
    chromium: true,
    webSerial: hasWebSerial(nav),
    wifiToDevice: true,
    advice: TRANSPORT_OK,
  };
}
